import React, { useState, useEffect } from 'react';
import { IcLineChart, IcBanknote, IcWallet, IcShoppingCart, IcBoxes, IcTrophy, IcLayers, IcCalendarDays, IcCalendarRange, IcPackageOpen, IcSortDesc, IcSparkles } from '../../theme/icons';
import SalesTrendChart from '../../components/SalesTrendChart';
import WeekdayPanelChart, { bestWeekday } from '../../components/WeekdayPanelChart';
import statisticsService from '../../services/statisticsService';
import { peekCached } from '../../services/apiCache';
import { fmt, Delta, StatCard, Section, StatsChip, Spinner, HBarList } from './statsUi';
import { StatsFilterBar, DEFAULT_FILTER } from './StatsFilter';

// Hauteur du viewBox des deux graphes de la grande carte (largeur fixe 1000).
// 460 = la hauteur d'ORIGINE (owner 2026-09-07 : « pourquoi avoir réduit la case ?
// remets-la »). Un essai à 250 a été refusé. Le gabarit du cadre (aspect-ratio
// inline) lit la MÊME constante : impossible de les désynchroniser.
const STATS_CHART_H = 460;

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

// Top produits table — full-width card, its own place (owner). Column headers
// Qté / CA / Bénéfice are clickable to re-sort; the backend sends the union
// of the top 10 by each criterion, so every sort is accurate.
const TopProductsTable = ({ rows }) => {
  const [sortKey, setSortKey] = useState('revenue');
  if (!rows || rows.length === 0) {
    return <p className="text-gray-400 text-sm text-center py-8">Aucune vente sur la période.</p>;
  }
  const sorted = [...rows].sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0)).slice(0, 10);
  const SortTh = ({ k, label }) => (
    <th className="th-ki py-2 text-right"
      aria-sort={sortKey === k ? 'descending' : 'none'}>
      <button type="button" onClick={() => setSortKey(k)} title={`Trier par ${label}`}
        className={`inline-flex items-center gap-1 cursor-pointer transition-colors ${
          sortKey === k ? 'text-green-700 font-semibold' : 'hover:text-gray-900'
        }`}>
        {label} <IcSortDesc size={9} className={sortKey === k ? '' : 'opacity-0'} />
      </button>
    </th>
  );
  // Tableau dense (Composants-2 §6) : cadre #E5E7EB rayon 6, en-tête th-ki,
  // lignes 13px 8/12px, nom 600, montants à droite tabulaires 600 encre.
  return (
    <div className="overflow-x-auto border border-gray-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left">
            <th className="th-ki py-2 w-8">#</th>
            <th className="th-ki py-2">Produit</th>
            <SortTh k="qty" label="Qté" />
            <SortTh k="revenue" label="CA" />
            <SortTh k="benefice" label="Bénéfice" />
          </tr>
        </thead>
        <tbody>
          {sorted.map((t, i) => (
            <tr key={t.name} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-100 transition-colors">
              <td className="td-ki py-2 text-gray-400 tabular-nums">{i + 1}</td>
              <td className="td-ki py-2 pr-2 font-semibold text-gray-900 truncate max-w-[260px]">{t.name}</td>
              <td className="td-ki py-2 text-right text-gray-500 tabular-nums">{t.qty}</td>
              <td className="td-ki py-2 text-right font-semibold text-gray-900 tabular-nums">{fmt(t.revenue)}</td>
              <td className={`td-ki py-2 text-right font-semibold tabular-nums ${
                (t.benefice || 0) < 0 ? 'text-red-600' : 'text-gray-900'
              }`}>{fmt(t.benefice)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SalesStatistics = () => {
  // filter = { period } (période rapide) OU { start, end } (plage libre).
  const [filter, setFilter] = useState(DEFAULT_FILTER);
  // Une seule grande carte, 4 vues (owner 2026-09-07 : « Top produits avec la courbe,
  // dans la même case, pour moins défiler ») : evolution | weekday | products | categories
  const [mainChart, setMainChart] = useState('evolution');
  // Last known answer painted at once; the fetch below refreshes it.
  const [data, setData] = useState(() => peekCached('statistics/', DEFAULT_FILTER.period ? { period: DEFAULT_FILTER.period } : undefined) || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    statisticsService.getStatistics(filter.period || { start: filter.start, end: filter.end })
      .then((d) => { if (alive) { setData(d); setError(null); } })
      .catch(() => { if (alive) setError('Impossible de charger les statistiques.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [filter]);

  const s = data?.summary || {};
  const p = data?.prev || {};

  return (
    <div className="w-full px-4 pt-2 pb-4">
      <StatsChip icon={IcLineChart} title="Statistique de ventes" />

      <StatsFilterBar filter={filter} onChange={setFilter} />

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-4 mb-4 rounded-xl">{error}</div>}

      {loading && !data ? <Spinner /> : (
        <>
          {/* KPI — ordre owner 2026-09-07 (remplace celui du 2026-08-16) :
              Ventes → CA → Encaissé → Articles vendus (+ soins) → Coût des
              ventes → Bénéfice brut. La case double « Articles | Services »
              remonte en 4e position ; le couple Coût → Bénéfice ferme la
              rangée. */}
          {/* Cartes KPI blanches = information (Composants-2 §5). */}
          {/* Owner 2026-09-10 : même hauteur de cartes que /statistiques/generale
              → disposition « Stock » (layout="stock" : icône + libellé centrés,
              valeur 18px centrée, sous-ligne sous un filet). La case double
              Articles | Services suit le même squelette, en 2 colonnes.
              Couleurs inchangées. */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
            <StatCard icon={IcShoppingCart} label="Ventes" layout="stock"
              value={`${s.count || 0}`} curr={s.count} prev={p.count}
              sub={`Panier moyen ${fmt(s.avg_ticket)}`} />
            <StatCard icon={IcBanknote} label="Chiffre d'affaires" layout="stock"
              value={fmt(s.ca)} curr={s.ca} prev={p.ca} valueClassName="text-green-900" />
            <StatCard icon={IcWallet} label="Encaissé" layout="stock"
              value={fmt(s.encaisse)} curr={s.encaisse} prev={p.encaisse} valueClassName="text-green-700"
              sub={`Crédit donné ${fmt(s.credit)}`} />
            {/* Case divisée en 2 (owner) : Articles vendus | Services. */}
            <div className="kpi min-w-0 text-center">
              <div className="grid grid-cols-2 divide-x divide-gray-100">
                <div className="pr-3 min-w-0">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="kpi-icon static h-7 w-7 shrink-0 rounded-lg flex items-center justify-center"><IcBoxes size={12} /></span>
                    <span className="kpi-label uppercase">Articles</span>
                  </div>
                  <div className="flex items-baseline justify-center gap-2 min-w-0">
                    <span className="kpi-value text-lg truncate">{s.qty_sold || 0}</span>
                    <Delta curr={s.qty_sold} prev={p.qty_sold} />
                  </div>
                </div>
                <div className="pl-3 min-w-0">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="kpi-icon static h-7 w-7 shrink-0 rounded-lg flex items-center justify-center"><IcSparkles size={12} /></span>
                    <span className="kpi-label uppercase">Services</span>
                  </div>
                  <div className="flex items-baseline justify-center gap-2 min-w-0">
                    <span className="kpi-value text-lg truncate">{s.soins || 0}</span>
                    <Delta curr={s.soins} prev={p.soins} />
                  </div>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-gray-100 grid grid-cols-2 divide-x divide-gray-100 text-[11px] text-center">
                <div className="pr-3 kpi-sub truncate">Remises {fmt(s.discount)}</div>
                <div className="pl-3 kpi-sub truncate">{fmt(s.soins_total)}</div>
              </div>
            </div>
            <StatCard icon={IcPackageOpen} label="Coût des ventes" layout="stock"
              value={fmt(s.capital)} curr={s.capital} prev={p.capital} invert valueClassName="text-red-700"
              sub="Capital marchandise vendu" />
            <StatCard icon={IcLineChart} label="Bénéfice brut" layout="stock"
              value={fmt(s.benefice)} curr={s.benefice} prev={p.benefice} valueClassName="text-amber-700"
              sub={`Marge ${(s.marge || 0).toFixed(0)}% du CA`} />
          </div>

          {/* UNE grande carte, 4 vues au même endroit (owner 2026-09-07) : la courbe
              Évolution, le CA par jour de la semaine, le tableau Top produits et le CA
              par catégorie se remplacent dans le même boîtier → moins de défilement.
              La courbe reprend la disposition du tableau de bord (légende SOUS la
              courbe, centrée : `legend="bottom"`). */}
          {(() => {
            const VIEWS = {
              evolution:  { icon: IcLineChart,     tone: 'green', title: 'Évolution — CA · Coût · Bénéfice' },
              weekday:    { icon: IcCalendarRange, tone: 'green', title: 'CA par jour de la semaine' },
              products:   { icon: IcTrophy,        tone: 'amber', title: 'Top produits vendus' },
              categories: { icon: IcLayers,        tone: 'green', title: 'CA par catégorie' },
            };
            const v = VIEWS[mainChart] || VIEWS.evolution;
            return (
              <Section
                icon={v.icon}
                tone={v.tone}
                title={v.title}
                right={(() => {
                  /* « Meilleur jour » dans l'ENTÊTE, à droite, pour les deux graphes
                     (owner 2026-09-07 : il était SOUS les barres sur la vue Jour de la
                     semaine et creusait un grand vide au-dessus du pied de carte). */
                  if (mainChart === 'evolution' && data?.best_day) {
                    return (
                      <span className="hidden md:inline-flex items-center gap-1">
                        <IcCalendarDays /> Meilleur jour : {fmtDate(data.best_day.date)} ({fmt(data.best_day.ca)})
                      </span>
                    );
                  }
                  if (mainChart === 'weekday') {
                    const bw = bestWeekday(data?.by_weekday);
                    return bw ? (
                      <span className="hidden md:inline-flex items-center gap-1">
                        <IcCalendarDays /> Meilleur jour : <span className="font-semibold text-gray-900">{bw.label}</span>
                        <span className="tabular-nums">({bw.tick})</span>
                      </span>
                    ) : null;
                  }
                  return null;
                })()}
                className="mb-4">
                {/* Boîtier à TAILLE FIXE (owner 2026-09-07) : la case ne change pas quand on
                    bascule, seules les données changent — et SANS mou (2e passe « encore
                    moins d'espace ») : un « gabarit » invisible fixe la hauteur à EXACTEMENT
                    ce qu'occupe la vue la plus haute, la courbe = son SVG (viewBox 1000×STATS_CHART_H,
                    donc largeur × 0,46) + sa légende (mt-3 12 px + ligne 16 px = 28 px,
                    `mb-7` = 28 px, sans garde). Un ratio seul ne peut pas exprimer
                    « 0,46 × largeur + 28 px » : c'était la source du vide. Les 4 vues se
                    posent en `absolute inset-0` dessus ; tableau et catégories y défilent. */}
                <div className="relative min-w-0 overflow-hidden">
                  <div aria-hidden="true" className="mb-7 pointer-events-none" style={{ aspectRatio: `1000 / ${STATS_CHART_H}` }} />
                  <div className={`absolute inset-0${mainChart === 'products' || mainChart === 'categories' ? ' overflow-y-auto' : ''}`}>
                    {mainChart === 'weekday' && <WeekdayPanelChart rows={data?.by_weekday} light caption={false} height={STATS_CHART_H} />}
                    {mainChart === 'products' && <TopProductsTable rows={data?.top_products} />}
                    {mainChart === 'categories' && (
                      <HBarList tone="green" cols={2}
                        rows={(data?.by_category || []).map((c) => ({
                          name: c.name, value: c.revenue, extra: `${c.qty || 0} article(s)`,
                        }))}
                      />
                    )}
                    {(mainChart === 'evolution' || !VIEWS[mainChart]) && <SalesTrendChart series={data?.series} light legend="bottom" height={STATS_CHART_H} />}
                  </div>
                </div>
                {/* Bascule SOUS le graphe = le PIED DE PAGINATION de la liste des produits
                    (ProductList.jsx « Pagination footer », Composants-2 §4) : bandeau pleine
                    largeur de la carte (marges négatives = padding p-5 de Section), filet
                    haut gray-100, boutons pg-on / pg-btn à DROITE (owner 2026-09-07). */}
                <div className="mt-2 -mx-5 -mb-5 px-5 py-4 border-t border-gray-100 bg-white rounded-b-2xl flex items-center gap-3">
                  <div className="flex-1" />
                  <div className="inline-flex items-center gap-1" role="tablist" aria-label="Vue affichée">
                  {[
                    { key: 'evolution', label: 'Évolution' },
                    { key: 'weekday', label: 'Jour de la semaine' },
                    { key: 'products', label: 'Top produits' },
                    { key: 'categories', label: 'Catégories' },
                  ].map((o) => (
                    <button key={o.key} type="button" role="tab" aria-selected={mainChart === o.key}
                      onClick={() => setMainChart(o.key)}
                      className={`${mainChart === o.key ? 'pg-on' : 'pg-btn'} w-auto px-3`}>
                      {o.label}
                    </button>
                  ))}
                  </div>
                </div>
              </Section>
            );
          })()}
        </>
      )}
    </div>
  );
};

export default SalesStatistics;
